'use client';

// Composer (new thread) and reply box for /community. Both need a sign-in and a Cloudflare
// Turnstile token; the server runs the rest of the spam checks (src/lib/community).

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import {
  COMMUNITY_CATEGORIES,
  COMMUNITY_LIMITS,
  type CommunitySubmitResult,
} from '@/lib/community/shared';

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';
const STAFF_ROLES = new Set(['owner', 'editor', 'support']);

type TurnstileApi = {
  render(element: HTMLElement, options: Record<string, unknown>): string;
  reset(id: string): void;
  remove(id: string): void;
};

let turnstileScript: Promise<TurnstileApi | null> | null = null;

function loadTurnstile(): Promise<TurnstileApi | null> {
  if (!SITE_KEY) return Promise.resolve(null);
  const existing = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
  if (existing) return Promise.resolve(existing);
  turnstileScript ??= new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () =>
      resolve((window as unknown as { turnstile?: TurnstileApi }).turnstile ?? null);
    script.onerror = () => resolve(null);
    document.head.append(script);
  });
  return turnstileScript;
}

/** Renders an invisible-by-default Turnstile widget and exposes its latest token. */
function useTurnstile(active: boolean) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(SITE_KEY ? null : 'dev');

  useEffect(() => {
    if (!active || !SITE_KEY) return;
    let cancelled = false;
    void loadTurnstile().then((api) => {
      if (!api || cancelled || !containerRef.current || widgetRef.current) return;
      widgetRef.current = api.render(containerRef.current, {
        sitekey: SITE_KEY,
        appearance: 'interaction-only',
        callback: (value: string) => setToken(value),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
      });
    });
    return () => {
      cancelled = true;
      const api = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (api && widgetRef.current) api.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [active]);

  const reset = useCallback(() => {
    const api = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    if (api && widgetRef.current) {
      setToken(null);
      api.reset(widgetRef.current);
    }
  }, []);

  return { containerRef, token, reset };
}

function SignInPrompt({ next, verb }: { next: string; verb: string }) {
  return (
    <div className="community-signin">
      <p>
        <strong>Sign in to {verb}.</strong> One click with Google or Meta. We only show your first
        name and last initial.
      </p>
      <Link className="community-button" href={`/login?next=${encodeURIComponent(next)}`}>
        Sign in
      </Link>
    </div>
  );
}

async function post(url: string, body: Record<string, unknown>, idToken: string) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  return (await response.json().catch(() => ({
    ok: false,
    message: 'Something went wrong. Please try again.',
  }))) as CommunitySubmitResult;
}

export function ThreadComposer({
  defaultCategory = 'help',
  toolSlug,
  toolTitle,
}: {
  defaultCategory?: string;
  toolSlug?: string;
  toolTitle?: string;
}) {
  const router = useRouter();
  const { user, isLoading, session } = useFirebaseAuth();
  const isStaff = STAFF_ROLES.has(session?.account?.role ?? '');
  const [category, setCategory] = useState(defaultCategory);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const { containerRef, token, reset } = useTurnstile(Boolean(user));

  if (isLoading) return <div className="community-card community-muted">Loading…</div>;
  if (!user) {
    const next = `/community/new${toolSlug ? `?tool=${toolSlug}` : ''}`;
    return <SignInPrompt next={next} verb="start a thread" />;
  }

  const categories = COMMUNITY_CATEGORIES.filter((item) => !item.staffOnly || isStaff);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await post(
        '/api/community/threads',
        { category, title, body, toolSlug, turnstileToken: token },
        await user.getIdToken(),
      );
      if (result.ok) {
        setMessage({ tone: 'ok', text: result.message });
        if (result.status === 'published') router.push(result.path);
        else {
          setTitle('');
          setBody('');
        }
      } else {
        setMessage({ tone: 'error', text: result.message });
      }
    } finally {
      setBusy(false);
      reset();
    }
  }

  return (
    <form className="community-card community-form" onSubmit={(event) => void submit(event)}>
      {toolTitle ? (
        <p className="community-muted">
          About <strong>{toolTitle}</strong>
        </p>
      ) : null}
      <label className="community-field">
        <span>Category</span>
        <select onChange={(event) => setCategory(event.target.value)} value={category}>
          {categories.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <label className="community-field">
        <span>Title</span>
        <input
          maxLength={COMMUNITY_LIMITS.titleMax}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="HEIC to JPG keeps failing on one photo"
          required
          value={title}
        />
      </label>
      <label className="community-field">
        <span>Details</span>
        <textarea
          maxLength={COMMUNITY_LIMITS.bodyMax}
          onChange={(event) => setBody(event.target.value)}
          placeholder="What did you try, what happened, and what did you expect? Mention the file type and your browser."
          required
          rows={8}
          value={body}
        />
        <small>Plain text. Wrap code in ``` and it keeps its formatting.</small>
      </label>
      <div ref={containerRef} />
      <div className="community-actions">
        <button className="community-button" disabled={busy || !token} type="submit">
          {busy ? 'Posting…' : token ? 'Post thread' : 'Checking you are human…'}
        </button>
        {message ? (
          <p className={message.tone === 'ok' ? 'community-ok' : 'community-error'} role="status">
            {message.text}
          </p>
        ) : null}
      </div>
    </form>
  );
}

export function ReplyBox({ threadId, threadPath }: { threadId: string; threadPath: string }) {
  const router = useRouter();
  const { user, isLoading } = useFirebaseAuth();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const { containerRef, token, reset } = useTurnstile(Boolean(user));

  if (isLoading) return null;
  if (!user) return <SignInPrompt next={threadPath} verb="reply" />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await post(
        `/api/community/threads/${threadId}/replies`,
        { body, turnstileToken: token },
        await user.getIdToken(),
      );
      setMessage({ tone: result.ok ? 'ok' : 'error', text: result.message });
      if (result.ok) {
        setBody('');
        if (result.status === 'published') router.refresh();
      }
    } finally {
      setBusy(false);
      reset();
    }
  }

  return (
    <form className="community-card community-form" onSubmit={(event) => void submit(event)}>
      <label className="community-field">
        <span>Your reply</span>
        <textarea
          maxLength={COMMUNITY_LIMITS.replyMax}
          onChange={(event) => setBody(event.target.value)}
          required
          rows={5}
          value={body}
        />
      </label>
      <div ref={containerRef} />
      <div className="community-actions">
        <button className="community-button" disabled={busy || !token} type="submit">
          {busy ? 'Posting…' : token ? 'Post reply' : 'Checking you are human…'}
        </button>
        {message ? (
          <p className={message.tone === 'ok' ? 'community-ok' : 'community-error'} role="status">
            {message.text}
          </p>
        ) : null}
      </div>
    </form>
  );
}

export function PostActions({
  threadId,
  postId,
  threadAuthorUid,
  canAccept,
}: {
  threadId: string;
  postId?: string;
  threadAuthorUid: string;
  canAccept: boolean;
}) {
  const router = useRouter();
  const { user, session } = useFirebaseAuth();
  const [note, setNote] = useState<string | null>(null);
  if (!user) return null;
  const isStaff = STAFF_ROLES.has(session?.account?.role ?? '');
  const mayAccept = canAccept && postId && (user.uid === threadAuthorUid || isStaff);

  async function act(url: string, body: Record<string, unknown>) {
    if (!user) return;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify(body),
    });
    const result = (await response.json().catch(() => ({}))) as { ok?: boolean; message?: string };
    setNote(result.message ?? null);
    if (result.ok && url.endsWith('/accept')) router.refresh();
  }

  return (
    <div className="community-post-actions">
      {mayAccept ? (
        <button
          onClick={() => void act(`/api/community/threads/${threadId}/accept`, { postId })}
          type="button"
        >
          Mark as answer
        </button>
      ) : null}
      <button
        onClick={() => {
          const reason = window.prompt('What is wrong with this post? (spam, abuse, other)');
          if (reason !== null) {
            void act('/api/community/report', {
              targetType: postId ? 'post' : 'thread',
              targetId: postId ?? threadId,
              reason,
            });
          }
        }}
        type="button"
      >
        Report
      </button>
      {note ? <span className="community-muted">{note}</span> : null}
    </div>
  );
}

/** The news composer, shown only to the Cleanor team. */
export function StaffNewsComposer() {
  const { session } = useFirebaseAuth();
  if (!STAFF_ROLES.has(session?.account?.role ?? '')) return null;
  return (
    <details className="community-card">
      <summary>Post news (team only)</summary>
      <ThreadComposer defaultCategory="news" />
    </details>
  );
}
